import { Configuration } from '@/api';
import { HttpStatus } from '@/api/HttpStatus';
import {
  toPaymentMethod,
  toPaymentMethods,
  toPaymentMethodSetup,
} from '@/api/mappers/mapPaymentMethod';
import {
  PaymentsApi as ApiPaymentsApi,
  DeletePaymentMethodRequest,
  DisablePaymentMethodRequest,
  EnablePaymentMethodRequest,
  ResponseError,
  SetDefaultPaymentMethodRequest,
} from '@/gen/api';
import { PaymentProviderStatus } from '@/models/Payment';
import { PaymentMethod, PaymentMethods, PaymentMethodSetup } from '@/models/PaymentMethod';

interface PaymentsApi {
  listPaymentMethods(): Promise<PaymentMethods>;
  createPaymentMethodSetup(): Promise<PaymentMethodSetup>;
  getPaymentProviderStatus(): Promise<PaymentProviderStatus>;
  enablePaymentMethod(id: string): Promise<void>;
  disablePaymentMethod(id: string): Promise<void>;
  deletePaymentMethod(id: string): Promise<void>;
  getDefaultPaymentMethod(): Promise<PaymentMethod | undefined>;
  setDefaultPaymentMethod(id: string): Promise<void>;
}

export class PaymentsApiImpl implements PaymentsApi {
  private paymentsApi: ApiPaymentsApi;

  constructor(configuration: Configuration) {
    this.paymentsApi = new ApiPaymentsApi(configuration);
  }

  async listPaymentMethods(): Promise<PaymentMethods> {
    try {
      const response = await this.paymentsApi.listPaymentMethods();
      return toPaymentMethods(response);
    } catch (error) {
      console.error('Error listing payment methods:', error);
      throw error;
    }
  }

  async createPaymentMethodSetup(): Promise<PaymentMethodSetup> {
    try {
      const response = await this.paymentsApi.createPaymentMethodSetup();
      return toPaymentMethodSetup(response);
    } catch (error) {
      console.error('Failed to create payment method setup:', error);
      throw error;
    }
  }

  async getPaymentProviderStatus(): Promise<PaymentProviderStatus> {
    try {
      return await this.paymentsApi.getPaymentProviderStatus();
    } catch (error) {
      console.error('Failed to get payment provider status:', error);
      throw error;
    }
  }

  async deletePaymentMethod(id: string): Promise<void> {
    try {
      const req: DeletePaymentMethodRequest = { id };
      return await this.paymentsApi.deletePaymentMethod(req);
    } catch (error) {
      console.error('Failed to delete payment method:', error);
      throw error;
    }
  }

  async enablePaymentMethod(id: string): Promise<void> {
    try {
      const req: EnablePaymentMethodRequest = { id };
      return await this.paymentsApi.enablePaymentMethod(req);
    } catch (error) {
      console.error('Failed to enable payment method:', error);
      throw error;
    }
  }

  async disablePaymentMethod(id: string): Promise<void> {
    try {
      const req: DisablePaymentMethodRequest = { id };
      return await this.paymentsApi.disablePaymentMethod(req);
    } catch (error) {
      console.error('Failed to disable payment method:', error);
      throw error;
    }
  }

  async setDefaultPaymentMethod(id: string): Promise<void> {
    try {
      const req: SetDefaultPaymentMethodRequest = { id };
      return await this.paymentsApi.setDefaultPaymentMethod(req);
    } catch (error) {
      console.error('Failed to set default payment method:', error);
      throw error;
    }
  }

  async getDefaultPaymentMethod(): Promise<PaymentMethod | undefined> {
    try {
      const response = await this.paymentsApi.getDefaultPaymentMethod();
      return toPaymentMethod(response);
    } catch (error) {
      if (error instanceof ResponseError && error.response.status === HttpStatus.NotFound) {
        return;
      }

      console.error('Failed to get default payment method:', error);
      throw error;
    }
  }
}

export default PaymentsApi;
